import { useCallback, useEffect, useMemo, useState } from "react";
import { collection, query, writeBatch, doc, getDoc, getDocs, where, serverTimestamp, setDoc, updateDoc, deleteField, type WriteBatch } from "firebase/firestore";
import { withOrg, orgQuery, orgWhere, getCurrentOrgId } from "@/lib/org";
import { useAuth } from "@/store/auth";
import { diffRow, updatePayload, matchExisting, isSameStudent, missingFromFile, describeChange, type RowPlan } from "@/lib/importDiff";
import { db } from "@/lib/firebase";
import { ALL_BRANDS, Brand, Student } from "@/types";
import { parseFileToStudents, deriveBuildingName } from "@/lib/excelParse";
import { Issue, validateRow, worstLevel, rowDocId, normalizePhone, needsConfirm, CONFIRM_CODES } from "@/lib/importValidate";
import { geocodeAddress } from "@/lib/vworld";
import { BRAND_COLORS } from "@/lib/brands";
import clsx from "clsx";
import { parseGuFrom } from "@/lib/region";

import { plainError } from "@/lib/plainError";
interface ImportRow {
  id: string;
  sourceFile: string;
  data: Partial<Student>;
  issues: Issue[];
  selected: boolean;
  geocoding?: boolean;
  uploaded?: boolean;
}

interface Target { id: string; existing: Student | null } // 이 행이 갈 문서와, 있으면 그 기존 학생
interface PlannedRow extends Target { plan: RowPlan }
interface MissingStudent { id: string; school: string; name: string; status?: string }
interface UploadPlan {
  rows: Map<string, PlannedRow>;
  counts: { create: number; update: number; same: number };
  missing: MissingStudent[];
  skipUpdate: Set<string>; // 사람이 "파일 값으로 바꾸지 않기"로 뺀 행
}
interface ImportBatch { id: string; at: number; files?: string[]; createdCount?: number; updatedCount?: number; undone?: boolean }
const UNDO_WINDOW_MS = 7 * 24 * 3600 * 1000; // 일주일 지난 올리기는 그 뒤 배송·수정이 쌓여 통째로 되돌리면 더 위험하다

export default function ImportStudents() {
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [brand, setBrand] = useState<Brand>("서울우유");
  const [company, setCompany] = useState<string>("자사");
  const [isProxy, setIsProxy] = useState(false);
  const [proxyFor, setProxyFor] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<"all" | "error" | "warning" | "ok">("all");
  const [riskConfirmed, setRiskConfirmed] = useState(false); // 주소·호수 확인 필요 건을 사람이 검토했는지
  const [plan, setPlan] = useState<UploadPlan | null>(null); // 비교 결과(올리기 전 단계)
  const [batches, setBatches] = useState<ImportBatch[]>([]); // 최근 올리기 묶음(되돌리기용)
  const [confirmUndo, setConfirmUndo] = useState<string | null>(null);
  const user = useAuth((s) => s.user);

  // ── 파일 추가 ─────────────────────────────────────
  const addFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length) return;
    setBusy(true);
    try {
      const all: ImportRow[] = [];
      for (const f of list) {
        try {
          setLog((l) => [`📂 ${f.name} 파싱 중…`, ...l]);
          const parsed = await parseFileToStudents(f, { brand, company, isProxy, proxyFor, legacyGroups: getCurrentOrgId() === "freemilk" });
          parsed.forEach((p) => {
            all.push({
              id: `${f.name}:${all.length}`,
              sourceFile: f.name,
              data: p,
              issues: [],
              selected: true,
            });
          });
          setLog((l) => [
            parsed.length > 0
              ? `  ✓ ${f.name} — ${parsed.length}명`
              : `  ⚠ ${f.name} — 0명을 읽었습니다. 엑셀에 '학교'와 '이름(또는 성명)' 칸 제목이 있는지 확인해 주세요.`,
            ...l,
          ]);
        } catch (e: any) {
          setLog((l) => [`  ❌ ${f.name} — ${plainError(e)}`, ...l]);
        }
      }
      // 기존 + 신규 합치고 검증
      const merged = [...rows, ...all];
      const dataArr = merged.map((r) => r.data);
      const rechecked = merged.map((r) => ({ ...r, issues: validateRow(r.data, dataArr) }));
      setRows(rechecked);
      setRiskConfirmed(false); // 새 파일이 들어왔으므로 이전 확인은 무효
      setLog((l) => [`📊 총 ${rechecked.length}행 (누적)`, ...l]);
    } finally {
      setBusy(false);
    }
  };

  // ── 드래그 이벤트 ─────────────────────────────────
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const onDragLeave = () => setIsDragging(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  // ── 개별 행 수정 ──────────────────────────────────
  const updateRow = (id: string, patch: Partial<Student>) => {
    setRows((curr) => {
      const idx = curr.findIndex((r) => r.id === id);
      if (idx < 0) return curr;
      const next = [...curr];
      next[idx] = { ...next[idx], data: { ...next[idx].data, ...patch } };
      // 재검증
      const dataArr = next.map((r) => r.data);
      next.forEach((r, i) => {
        next[i] = { ...next[i], issues: validateRow(r.data, dataArr) };
      });
      return next;
    });
  };

  const removeRow = (id: string) => {
    setRows((curr) => {
      const next = curr.filter((r) => r.id !== id);
      const dataArr = next.map((r) => r.data);
      return next.map((r) => ({ ...r, issues: validateRow(r.data, dataArr) }));
    });
  };

  const toggleSelect = (id: string) => {
    setRows((curr) => curr.map((r) => (r.id === id ? { ...r, selected: !r.selected } : r)));
  };

  const toggleSelectAll = (checked: boolean) => {
    setRows((curr) => curr.map((r) => ({ ...r, selected: checked })));
  };

  // ── 주소 자동 정리 (V-World) ─────────────────────
  const geocodeOne = async (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    setRows((curr) => curr.map((r) => (r.id === id ? { ...r, geocoding: true } : r)));
    try {
      const addr = row.data.refinedAddress || row.data.rawAddress || "";
      const p = await geocodeAddress(addr);
      if (p?.roadMismatch) {
        // 입력과 다른 번길로 스냅됨(함정 #8) — 자동 수용하지 않고 사람이 주소를 고치게 남긴다
        setLog((l) => [`⚠️ ${row.data.name} 입력과 다른 길로 매칭(${p.matched}) — 주소 표기 확인 필요`, ...l]);
      } else if (p) {
        // refined 주소 재파싱
        const m = p.matched.match(/\(([가-힣0-9]+(?:동|가|리))(?:,|\))/);
        const dong = m ? m[1] : row.data.dong || "";
        const gu = row.data.gu || extractGu(p.matched);
        const bn = deriveBuildingName(p.matched);
        updateRow(id, {
          refinedAddress: p.matched,
          lat: p.y,
          lng: p.x,
          gu,
          dong,
          ...(bn ? { buildingName: bn } : {}),
        });
      } else {
        setLog((l) => [`⚠️ ${row.data.name} 지도 위치 못 찾음`, ...l]);
      }
    } catch (e: any) {
      setLog((l) => [`❌ ${row.data.name} 에러: ${plainError(e)}`, ...l]);
    } finally {
      setRows((curr) => curr.map((r) => (r.id === id ? { ...r, geocoding: false } : r)));
    }
  };

  const geocodeAll = async () => {
    // 지도 위치가 있어도 주소 정제가 안 됐으면 대상에 포함 — 형제자매 행에 지도 위치만 복사해 넣은 경우
    // refinedAddress가 raw 그대로 남아 단지 묶음에서 빠지는 함정 방지(함정 문서 #7).
    const needsRefine = (d: (typeof rows)[number]["data"]) =>
      !d.refinedAddress || d.refinedAddress === d.rawAddress || !d.refinedAddress.includes("(");
    const targets = rows.filter((r) => !r.data.lat || !r.data.lng || needsRefine(r.data));
    if (!targets.length) {
      setLog((l) => ["ℹ️ 위치를 찾을 행이 없습니다", ...l]);
      return;
    }
    setBusy(true);
    let ok = 0, fail = 0;
    for (let i = 0; i < targets.length; i++) {
      const t = targets[i];
      setLog((l) => [`🌐 ${i + 1}/${targets.length} — ${t.data.school} ${t.data.name}`, ...l.slice(0, 200)]);
      try {
        const p = await geocodeAddress(t.data.refinedAddress || t.data.rawAddress || "");
        if (p?.roadMismatch) {
          // 입력과 다른 번길로 스냅됨(함정 #8) — 자동 수용하지 않는다
          setLog((l) => [`⚠️ ${t.data.name} 입력과 다른 길로 매칭(${p.matched}) — 주소 표기 확인 필요`, ...l]);
          fail++;
        } else if (p) {
          const m = p.matched.match(/\(([가-힣0-9]+(?:동|가|리))(?:,|\))/);
          const bn = deriveBuildingName(p.matched);
          updateRow(t.id, {
            refinedAddress: p.matched,
            lat: p.y,
            lng: p.x,
            gu: t.data.gu || extractGu(p.matched),
            dong: m ? m[1] : t.data.dong,
            ...(bn ? { buildingName: bn } : {}),
          });
          ok++;
        } else {
          fail++;
        }
      } catch { fail++; }
    }
    setLog((l) => [`✓ 지도 위치 — 찾음 ${ok}, 못 찾음 ${fail}`, ...l]);
    setBusy(false);
  };

  // ── 전체 브랜드/회사 재지정 ───────────────────────
  const applyOverrides = () => {
    setRows((curr) => curr.map((r) => ({
      ...r,
      data: { ...r.data, brand, company, isProxy, proxyFor },
    })));
    setLog((l) => [`🏷️ 전체 행에 "${brand}/${company}${isProxy ? ` · 대행(${proxyFor})` : ""}" 적용`, ...l]);
  };

  // ── 기존 학생과 맞추기 ─────────────────────────────
  // rowDocId는 주소 앞 25자까지만 쓰므로 (1) 같은 단지의 동명이인은 ID가 충돌하고, (2) 학교가 주소 표기를 조금만 바꿔도 ID가 달라진다.
  // (1) 기존 문서와 전화·주소가 모두 다르면 별개 학생으로 보고 전화 끝 4자리를 붙여 분리한다.
  // (2) 기준 ID 문서의 주인이 아니면 같은 학교·이름의 기존 학생 중 전화나 주소가 같은 문서로 잇는다 — 같은 학생이 두 명 되지 않게(예행연습 02-10).
  const resolveTargets = async (toUpload: ImportRow[]): Promise<Map<string, Target>> => {
    const digits = (s?: string) => (s || "").replace(/\D/g, "");
    const suffixOf = (r: ImportRow, idx: number) => digits(r.data.phone).slice(-4) || String(idx + 1);
    const groups = new Map<string, ImportRow[]>();
    for (const r of toUpload) {
      const base = rowDocId(r.data);
      if (!groups.has(base)) groups.set(base, []);
      groups.get(base)!.push(r);
    }
    const byName = new Map<string, Student[]>(); // "학교|이름" → 같은 이름의 기존 학생들(한 번만 조회)
    const candidatesFor = async (r: ImportRow) => {
      const key = `${r.data.school || ""}|${r.data.name || ""}`;
      if (!byName.has(key)) {
        const snap = await getDocs(orgQuery("students", where("school", "==", r.data.school || ""), where("name", "==", r.data.name || "")));
        byName.set(key, snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as Student));
      }
      return byName.get(key)!;
    };
    const out = new Map<string, Target>();
    const taken = new Set<string>(); // 한 번 이은 기존 문서에 두 행을 잇지 않는다
    for (const [base, group] of groups) {
      const snap = await getDoc(doc(db, "students", base));
      const ex = snap.exists() ? ({ id: snap.id, ...(snap.data() as object) } as Student) : null;
      const owner = ex ? group.find((r) => isSameStudent(r.data, ex)) : group[0];
      for (let idx = 0; idx < group.length; idx++) {
        const r = group[idx];
        if (ex && r === owner) {
          out.set(r.id, { id: base, existing: ex });
          taken.add(base);
          continue;
        }
        const matched = matchExisting(r.data, (await candidatesFor(r)).filter((c) => c.id !== base && !taken.has(c.id)));
        if (matched) {
          out.set(r.id, { id: matched.id, existing: matched });
          taken.add(matched.id);
          continue;
        }
        if (!ex && r === owner) {
          out.set(r.id, { id: base, existing: null });
          continue;
        }
        // 접미사 ID가 이미 다른 학생 것이면 그 위에 만들면 안 된다(만들기=덮어쓰기, 되돌리기=삭제). 같은 학생이면 고치기로, 아니면 빈 ID를 찾는다(코덱스 검토)
        let cand = `${base}_${suffixOf(r, idx)}`;
        let assigned = false;
        for (let n = 0; n < 5 && !assigned; n++) {
          const cs = await getDoc(doc(db, "students", cand));
          if (!cs.exists()) { assigned = true; break; }
          const cex = { id: cs.id, ...(cs.data() as object) } as Student;
          if (!taken.has(cand) && isSameStudent(r.data, cex)) { out.set(r.id, { id: cand, existing: cex }); taken.add(cand); assigned = true; break; }
          cand = `${base}_${suffixOf(r, idx)}_${n + 2}`;
        }
        if (out.has(r.id)) continue;
        out.set(r.id, { id: cand, existing: null });
        taken.add(cand);
      }
    }
    return out;
  };

  // ── 1단계: 기존 명단과 비교(아직 아무것도 쓰지 않는다) ─────────
  const prepare = async () => {
    const toUpload = rows.filter((r) => r.selected && !r.uploaded && !r.issues.some((i) => i.level === "error"));
    if (!toUpload.length) { setLog((l) => ["⚠️ 올릴 학생이 없습니다 (오류 행 제외)", ...l]); return; }
    // 관문 — 호수·번길이 조용히 버려진 건은 사람이 한 번 보고 넘겨야 한다.
    // 그냥 저장하면 몇 달간 아무도 모른 채 오배송으로 이어진다(2026-08-15).
    const risky = toUpload.filter((r) => needsConfirm(r.issues));
    if (risky.length && !riskConfirmed) {
      setLog((l) => [`🛑 확인 필요 ${risky.length}건 — 아래 노란 상자에서 검토 후 체크해야 올릴 수 있습니다: ${risky.slice(0, 5).map((r) => r.data.name).join(", ")}${risky.length > 5 ? " 외" : ""}`, ...l]);
      setFilter("warning");
      return;
    }
    setBusy(true);
    try {
      const targets = await resolveTargets(toUpload);
      const planned = new Map<string, PlannedRow>();
      const counts = { create: 0, update: 0, same: 0 };
      for (const r of toUpload) {
        const t = targets.get(r.id)!;
        const plan = diffRow(r.data, t.existing);
        planned.set(r.id, { ...t, plan });
        counts[plan.action]++;
      }
      // 이번 파일에 없는 기존 학생 — 파일에 나온 학교만 본다. 지우지 않고 보여만 준다.
      const schools = [...new Set(toUpload.map((r) => r.data.school || ""))].filter(Boolean);
      const plannedIds = new Set([...planned.values()].map((v) => v.id));
      const existingOfSchools: MissingStudent[] = [];
      for (const school of schools) {
        const snap = await getDocs(orgQuery("students", where("school", "==", school)));
        snap.forEach((d) => existingOfSchools.push({ id: d.id, school, name: String(d.get("name") || ""), status: d.get("status") }));
      }
      const missing = missingFromFile(existingOfSchools, plannedIds);
      setPlan({ rows: planned, counts, missing, skipUpdate: new Set() });
      setLog((l) => [`🔍 기존 명단과 비교: 새로 ${counts.create}명 · 바뀜 ${counts.update}명 · 그대로 ${counts.same}명 · 이번 파일에 없음 ${missing.length}명. 아래 표를 보고 「이대로 올리기」를 누르세요.`, ...l]);
    } catch (e: any) {
      setLog((l) => [`❌ 비교 오류: ${plainError(e)}`, ...l]);
    } finally {
      setBusy(false);
    }
  };

  // ── 2단계: 올리기 — 새 학생은 만들고, 기존 학생은 바뀐 칸만 고친다. 되돌릴 수 있게 묶음을 남긴다 ──
  const commit = async () => {
    if (!plan) return;
    setBusy(true);
    try {
      const target = rows.filter((r) => plan.rows.has(r.id));
      const batchRef = doc(collection(db, "importBatches"));
      const files = [...new Set(target.map((r) => r.sourceFile))];
      const ops: Array<{ rowId: string; run: (b: WriteBatch) => void }> = [];
      let created = 0, updated = 0, kept = 0;
      const writtenRows: ImportRow[] = [];
      for (const r of target) {
        const t = plan.rows.get(r.id)!;
        const sref = doc(db, "students", t.id);
        const rowRef = doc(collection(batchRef, "rows"), t.id);
        if (t.plan.action === "create") {
          ops.push({ rowId: r.id, run: (b) => {
            b.set(sref, withOrg({ ...r.data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }));
            b.set(rowRef, withOrg({ action: "create", studentId: t.id, before: null }));
          } });
          created++;
          writtenRows.push(r);
        } else if (t.plan.action === "update" && !plan.skipUpdate.has(r.id)) {
          const { set, clear } = updatePayload(r.data, t.plan);
          const upd: Record<string, unknown> = { ...set, updatedAt: serverTimestamp() };
          for (const f of clear) upd[f] = deleteField();
          const { id: _omit, ...before } = t.existing!;
          ops.push({ rowId: r.id, run: (b) => {
            b.update(sref, upd);
            b.set(rowRef, withOrg({ action: "update", studentId: t.id, before }));
          } });
          updated++;
          writtenRows.push(r);
        } else {
          kept++;
        }
      }
      if (!ops.length) {
        setLog((l) => ["ℹ️ 바꿀 것이 없습니다 — 기존 명단과 같습니다.", ...l]);
        setRows((curr) => curr.map((r) => (plan.rows.has(r.id) ? { ...r, uploaded: true } : r)));
        setPlan(null);
        return;
      }
      // 묶음 문서를 먼저 쓴다 — 행 기록만 있고 묶음이 없으면 되돌리기 목록에 안 나온다
      await setDoc(batchRef, withOrg({ createdAt: serverTimestamp(), createdBy: user?.uid || "", files, createdCount: created, updatedCount: updated, undone: false }));
      const PER = 200; // 학생 1 + 기록 1 = 2쓰기 → 한 묶음 400, Firestore 한도 500 안
      // 중간에 실패하면 성공한 구간은 올린 것으로 표시하고 남은 행만 비교표에 남긴다 — 다시 누르면 남은 행만 새 묶음으로 올라간다(중복 생성 방지, 코덱스 검토)
      const doneIds = new Set<string>();
      try {
        for (let i = 0; i < ops.length; i += PER) {
          const b = writeBatch(db);
          const slice = ops.slice(i, i + PER);
          slice.forEach((op) => op.run(b));
          await b.commit();
          slice.forEach((op) => doneIds.add(op.rowId));
          setLog((l) => [`✓ ${Math.min(i + PER, ops.length)} / ${ops.length} 저장됨`, ...l]);
        }
      } catch (e) {
        const left = new Map([...plan.rows].filter(([id]) => !doneIds.has(id)));
        const doneCreated = ops.filter((op) => doneIds.has(op.rowId) && plan.rows.get(op.rowId)!.plan.action === "create").length;
        setRows((curr) => curr.map((r) => (doneIds.has(r.id) ? { ...r, uploaded: true } : r)));
        setPlan({ ...plan, rows: left, counts: { create: plan.counts.create - doneCreated, update: plan.counts.update - (doneIds.size - doneCreated), same: 0 } });
        await updateDoc(batchRef, withOrg({ createdCount: doneCreated, updatedCount: doneIds.size - doneCreated, partial: true })).catch(() => undefined);
        throw new Error(`${doneIds.size}명까지 저장된 뒤 끊겼습니다(${(e as Error).message}). 남은 ${left.size}명은 비교표에 남겨 두었으니 「이대로 올리기」를 다시 누르세요.`);
      }
      setRows((curr) => curr.map((r) => (plan.rows.has(r.id) ? { ...r, uploaded: true } : r)));
      setLog((l) => [`🎉 올리기 완료 — 새로 ${created}명, 고침 ${updated}명, 그대로 ${kept}명. 잘못 올렸으면 아래 「방금 올린 것 되돌리기」.`, ...l]);
      setPlan(null);
      await loadBatches();
      // 게이트: 지도 위치/동 이름 미상으로 올라간 건은 배송 경로에서 빠지므로 즉시 경고
      const atRisk = writtenRows.filter((r) => !r.data.lat || !r.data.lng || !r.data.dong);
      if (atRisk.length > 0) {
        setLog((l) => [
          `⚠️ ${atRisk.length}명은 지도 위치/동 이름 없이 저장됨 → 배송 경로에서 빠집니다. 「지도 위치 찾기」 뒤에도 남으면 '주소 확인 대기'에서 처리하세요.`,
          ...l,
        ]);
      } else {
        setLog((l) => ["✅ 전원 지도 위치·동 이름 확보됨", ...l]);
      }
    } catch (e: any) {
      setLog((l) => [`❌ 오류: ${plainError(e)}`, ...l]);
    } finally {
      setBusy(false);
    }
  };

  // ── 되돌리기: 그때 만든 학생은 지우고, 고친 학생은 고치기 전 문서로 통째로 되돌린다 ──
  const loadBatches = useCallback(async () => {
    try {
      const snap = await getDocs(orgQuery("importBatches"));
      const list = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as Omit<ImportBatch, "id" | "at">), at: d.get("createdAt")?.toMillis?.() ?? 0 }))
        .sort((a, b) => b.at - a.at)
        .slice(0, 5);
      setBatches(list);
    } catch {
      setBatches([]);
    }
  }, []);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const undoBatch = async (batchId: string) => {
    if (confirmUndo !== batchId) { setConfirmUndo(batchId); return; }
    setConfirmUndo(null);
    setBusy(true);
    try {
      // 규칙이 orgId 조건 없는 목록 조회를 거부한다 — 자기 업체 행만 있어도 조건은 붙여야 한다(코덱스 검토)
      const rowsSnap = await getDocs(query(collection(db, "importBatches", batchId, "rows"), orgWhere()));
      const ops: Array<(b: WriteBatch) => void> = [];
      rowsSnap.forEach((d) => {
        const { action, studentId, before } = d.data() as { action: "create" | "update"; studentId: string; before: Record<string, unknown> | null };
        const sref = doc(db, "students", studentId);
        if (action === "create") ops.push((b) => b.delete(sref));
        else if (before) ops.push((b) => b.set(sref, withOrg({ ...before, updatedAt: serverTimestamp() })));
      });
      const PER = 400;
      for (let i = 0; i < ops.length; i += PER) {
        const b = writeBatch(db);
        ops.slice(i, i + PER).forEach((op) => op(b));
        await b.commit();
      }
      await updateDoc(doc(db, "importBatches", batchId), withOrg({ undone: true, undoneAt: serverTimestamp() }));
      setLog((l) => [`↩️ 되돌렸습니다 — ${ops.length}명을 올리기 전 상태로.`, ...l]);
      setRows([]);
      await loadBatches();
    } catch (e: any) {
      setLog((l) => [`❌ 되돌리기 오류: ${plainError(e)}`, ...l]);
    } finally {
      setBusy(false);
    }
  };

  // ── 필터 ──────────────────────────────────────────
  const filteredRows = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => {
      const w = worstLevel(r.issues);
      if (filter === "error") return w === "error";
      if (filter === "warning") return w === "warning";
      if (filter === "ok") return w === "ok" || w === "info";
      return true;
    });
  }, [rows, filter]);

  const counts = useMemo(() => {
    let err = 0, warn = 0, ok = 0;
    rows.forEach((r) => {
      const w = worstLevel(r.issues);
      if (w === "error") err++;
      else if (w === "warning") warn++;
      else ok++;
    });
    return { err, warn, ok, total: rows.length };
  }, [rows]);

  // 확인 없이 올리면 안 되는 행 — 선택됐고 오류가 아닌 것 중에서만 센다(업로드 대상과 같은 기준).
  const riskyRows = useMemo(
    () => rows.filter((r) => r.selected && !r.uploaded && !r.issues.some((i) => i.level === "error") && needsConfirm(r.issues)),
    [rows],
  );

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold">📥 학생 데이터 가져오기 (대량)</h1>

      {/* 비교표 — 올리기 전에 무엇이 바뀌는지 보고 고른다. 아직 아무것도 저장되지 않았다 */}
      {plan && (
        <section className="card p-4 space-y-3 border-2 border-milk-400">
          <div className="font-bold">기존 명단과 비교한 결과 — 아직 저장하지 않았습니다</div>
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="chip bg-emerald-100 text-emerald-800">새로 {plan.counts.create}명</span>
            <span className="chip bg-amber-100 text-amber-800">바뀜 {plan.counts.update - plan.skipUpdate.size}명</span>
            <span className="chip bg-slate-100 text-slate-700">그대로 {plan.counts.same}명</span>
            <span className="chip bg-rose-100 text-rose-800">이번 파일에 없음 {plan.missing.length}명</span>
          </div>
          {plan.counts.update > 0 && (
            <div className="text-sm">
              <div className="text-slate-600 mb-1">
                바뀐 학생은 <b>바뀐 칸만</b> 파일 값으로 고칩니다. 주소 검토에서 고친 위치, 택배 표시, 메모는 그대로 둡니다. 파일 값으로 바꾸고 싶지 않으면 체크를 빼세요.
              </div>
              <ul className="max-h-48 overflow-auto divide-y text-xs">
                {rows.filter((r) => plan.rows.get(r.id)?.plan.action === "update").map((r) => {
                  const p = plan.rows.get(r.id)!;
                  return (
                    <li key={r.id} className="py-1.5 flex gap-2 items-start">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={!plan.skipUpdate.has(r.id)}
                        onChange={(e) => {
                          const next = new Set(plan.skipUpdate);
                          if (e.target.checked) next.delete(r.id); else next.add(r.id);
                          setPlan({ ...plan, skipUpdate: next });
                        }}
                      />
                      <div>
                        <b>{r.data.school} {r.data.name}</b>
                        <div className="text-slate-600">{p.plan.changes.map(describeChange).join(" · ")}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {plan.missing.length > 0 && (
            <div className="text-sm">
              <div className="text-slate-600 mb-1">
                이번 파일에는 없는데 시스템에 있는 학생입니다. <b>지우지 않습니다.</b> 전학·졸업이면 학생 관리에서 전출 처리하세요.
              </div>
              <div className="max-h-24 overflow-auto text-xs text-slate-700">
                {plan.missing.slice(0, 60).map((m) => `${m.school} ${m.name}`).join(", ")}{plan.missing.length > 60 ? ` 외 ${plan.missing.length - 60}명` : ""}
              </div>
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            <button className="btn-primary" onClick={commit} disabled={busy}>
              이대로 올리기 (새로 {plan.counts.create} · 고침 {plan.counts.update - plan.skipUpdate.size})
            </button>
            <button className="btn-secondary" onClick={() => setPlan(null)} disabled={busy}>취소</button>
          </div>
        </section>
      )}

      {/* 최근 올리기 — 잘못 올렸으면 그때 상태로 되돌린다 */}
      {batches.length > 0 && (
        <section className="card p-4 text-sm space-y-2">
          <div className="font-semibold">최근 올리기</div>
          <ul className="divide-y">
            {batches.map((b) => {
              const canUndo = !b.undone && Date.now() - b.at < UNDO_WINDOW_MS;
              return (
                <li key={b.id} className="py-2 flex flex-wrap items-center gap-2">
                  <span className="text-slate-500">{b.at ? new Date(b.at).toLocaleString("ko-KR") : ""}</span>
                  <span>{(b.files || []).join(", ") || "파일"} — 새로 {b.createdCount ?? 0}명 · 고침 {b.updatedCount ?? 0}명</span>
                  {b.undone && <span className="chip bg-slate-100 text-slate-600">되돌림</span>}
                  {canUndo && (
                    <button className={clsx("btn-secondary text-xs ml-auto", confirmUndo === b.id && "border-rose-400 text-rose-700")} onClick={() => undoBatch(b.id)} disabled={busy}>
                      {confirmUndo === b.id ? "정말 되돌립니다 — 한 번 더 누르세요" : "이 올리기 되돌리기"}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="text-xs text-slate-500">되돌리면 그때 새로 만든 학생은 지워지고, 고친 학생은 올리기 전 내용으로 돌아갑니다. 일주일이 지난 올리기는 되돌릴 수 없습니다.</p>
        </section>
      )}

      {/* 설정 바 */}
      <section className={clsx(
        "card p-4 space-y-3 transition",
        isProxy ? "border-2 border-amber-400 bg-amber-50" : "",
      )}>
        {/* 타사 대행 토글 */}
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={isProxy}
            onChange={(e) => setIsProxy(e.target.checked)}
            className="w-4 h-4"
          />
          <span className="font-bold">🚛 타사 대행 물류</span>
          <span className="text-xs text-slate-500">
            (다른 회사의 우유 배송을 우리가 대행해주는 경우 — 상차 리스트에 별도 집계됩니다)
          </span>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <div className="label">{isProxy ? "대행할 우유 브랜드" : "브랜드"}</div>
            <select className="input mt-1" value={brand} onChange={(e) => setBrand(e.target.value as Brand)}>
              {ALL_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <div className="label">{isProxy ? "의뢰 회사명 *" : "물류 위탁업체 / 회사"}</div>
            <input
              className={clsx("input mt-1", isProxy && !proxyFor && "ring-2 ring-amber-400")}
              placeholder={isProxy ? "의뢰한 회사 (예: 연세우유, 남양유업 본사)" : "예: 자사, 연세우유대리점A"}
              value={isProxy ? proxyFor : company}
              onChange={(e) => isProxy ? setProxyFor(e.target.value) : setCompany(e.target.value)}
              list={isProxy ? "proxy-suggest" : "company-suggest"}
            />
            <datalist id="company-suggest">
              <option value="자사" />
              <option value="연세우유대리점" />
              <option value="남양유업대리점" />
              <option value="매일유업대리점" />
              <option value="동원F&B 대행" />
              <option value="파스퇴르 대행" />
              <option value="기타 대행" />
            </datalist>
            <datalist id="proxy-suggest">
              <option value="연세우유" />
              <option value="남양유업" />
              <option value="매일유업" />
              <option value="서울우유" />
              <option value="동원F&B (덴마크)" />
              <option value="파스퇴르" />
              <option value="빙그레" />
            </datalist>
          </div>
          <div>
            <div className="label">물류 집행 (우리 회사)</div>
            <input
              className="input mt-1"
              placeholder="예: 자사 / 하신배송"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              disabled={!isProxy}
            />
          </div>
          <button
            className="btn-secondary"
            onClick={applyOverrides}
            disabled={!rows.length || (isProxy && !proxyFor)}
          >
            위 값으로 전체 행 덮어쓰기
          </button>
        </div>

        {isProxy && (
          <div className="text-xs text-amber-800 bg-amber-100 rounded p-2">
            ℹ️ 이 파일의 학생들은 <strong>타사 대행</strong>으로 저장됩니다. 상차 리스트와 관제 화면에서 🚛 아이콘으로 구분됩니다.
            {!proxyFor && <span className="text-rose-600 font-bold"> (의뢰 회사명 필수)</span>}
          </div>
        )}
      </section>

      {/* 드래그앤드롭 */}
      <section
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={clsx(
          "border-2 border-dashed rounded-xl p-8 text-center transition",
          isDragging ? "border-milk-500 bg-milk-50" : "border-slate-300 bg-slate-50",
        )}
      >
        <div className="text-4xl mb-2">📂</div>
        <div className="font-bold">여기로 파일을 드래그하거나</div>
        <label className="btn-primary inline-block mt-2 cursor-pointer">
          파일 선택 (여러개 가능)
          <input
            type="file"
            className="hidden"
            multiple
            accept=".xlsx,.xls,.json"
            onChange={(e) => e.target.files && addFiles(e.target.files)}
          />
        </label>
        <div className="text-xs text-slate-500 mt-2">
          .xlsx · .xls · .json 지원 · 첫 줄의 칸 제목(학교·성명·연락처·주소 등)을 읽어 자동으로 맞춥니다
        </div>
      </section>

      {/* 요약 + 액션 바 */}
      {rows.length > 0 && (
        <section className="card p-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="chip bg-slate-100 text-slate-700">총 {counts.total}</span>
          <span className="chip bg-emerald-100 text-emerald-700">정상 {counts.ok}</span>
          <span className="chip bg-amber-100 text-amber-700">경고 {counts.warn}</span>
          <span className="chip bg-rose-100 text-rose-700">오류 {counts.err}</span>

          <div className="ml-auto flex gap-2 flex-wrap">
            <div className="inline-flex rounded-lg border overflow-hidden text-xs">
              {(["all", "ok", "warning", "error"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={clsx("px-3 py-1.5", filter === f ? "bg-milk-500 text-white" : "bg-white text-slate-600")}
                >
                  {f === "all" ? "전체" : f === "ok" ? "정상" : f === "warning" ? "경고" : "오류"}
                </button>
              ))}
            </div>
            <button className="btn-secondary text-xs" onClick={geocodeAll} disabled={busy}>
              🌐 주소 자동 정리
            </button>
            <button className="btn-ghost text-xs" onClick={() => setRows([])}>
              전체 비우기
            </button>
            <button className="btn-primary" onClick={prepare} disabled={busy || (!counts.ok && !counts.warn) || (riskyRows.length > 0 && !riskConfirmed)}>
              기존 명단과 비교해 올리기
            </button>
          </div>

          {/* 관문 — 호수·번길이 조용히 버려진 건은 사람이 보고 넘겨야 올라간다 */}
          {riskyRows.length > 0 && (
            <div className="mt-3 rounded-2xl border-2 border-amber-400 bg-amber-50 p-3">
              <div className="font-bold text-amber-900">
                🛑 확인 필요 {riskyRows.length}건 — 이대로 올리면 기사가 집을 못 찾을 수 있습니다
              </div>
              <div className="text-xs text-amber-800 mt-1">
                명단에는 있는데 시스템이 읽지 못한 정보입니다. 명단 원문을 보고 맞는지 확인하세요.
                아래 행의 <b>동·호</b> 칸을 직접 채우면 목록에서 빠집니다.
              </div>
              <ul className="mt-2 max-h-40 overflow-auto text-xs text-amber-900 space-y-1">
                {riskyRows.slice(0, 30).map((r) => (
                  <li key={r.id} className="border-t border-amber-200 pt-1">
                    <b>{r.data.school} {r.data.name}</b> — {r.issues.filter((i) => CONFIRM_CODES.has(i.code)).map((i) => i.message).join(" / ")}
                    <div className="text-amber-700">명단 원문: {r.data.rawAddress}</div>
                  </li>
                ))}
                {riskyRows.length > 30 && <li className="pt-1">…외 {riskyRows.length - 30}건</li>}
              </ul>
              <label className="flex items-center gap-2 mt-2 text-sm font-semibold text-amber-900 cursor-pointer">
                <input type="checkbox" checked={riskConfirmed} onChange={(e) => setRiskConfirmed(e.target.checked)} />
                위 {riskyRows.length}건을 확인했고, 이대로 올립니다
              </label>
            </div>
          )}
        </section>
      )}

      {/* 데이터 테이블 */}
      {filteredRows.length > 0 && (
        <div className="card overflow-auto max-h-[60vh]">
          <table className="min-w-full text-xs">
            <thead className="sticky top-0 bg-slate-100 text-slate-600 text-left z-10">
              <tr>
                <th className="p-2">
                  <input type="checkbox" checked={rows.every((r) => r.selected)} onChange={(e) => toggleSelectAll(e.target.checked)} />
                </th>
                <th className="p-2">상태</th>
                <th className="p-2">파일</th>
                <th className="p-2">학교</th>
                <th className="p-2">학생</th>
                <th className="p-2">보호자</th>
                <th className="p-2">전화</th>
                <th className="p-2">주소 (클릭 수정)</th>
                <th className="p-2">구/동</th>
                <th className="p-2">브랜드</th>
                <th className="p-2">업체</th>
                <th className="p-2">지도 위치</th>
                <th className="p-2">이슈</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <RowEditor
                  key={r.id}
                  row={r}
                  onToggle={() => toggleSelect(r.id)}
                  onChange={(p) => updateRow(r.id, p)}
                  onGeocode={() => geocodeOne(r.id)}
                  onRemove={() => removeRow(r.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 로그 */}
      <div className="card p-3 max-h-44 overflow-auto text-xs font-mono text-slate-600">
        {log.map((l, i) => <div key={i}>{l}</div>)}
      </div>
    </div>
  );
}

// 업체 권역 설정(긴 이름 우선)으로 구를 뽑는다 — 부산 목록을 쓰면 "달서구"가 "서구"로 바뀌었다
function extractGu(addr: string): string {
  return parseGuFrom(addr);
}

// ── RowEditor ─────────────────────────────────────────
function RowEditor({
  row, onToggle, onChange, onGeocode, onRemove,
}: {
  row: ImportRow;
  onToggle: () => void;
  onChange: (p: Partial<Student>) => void;
  onGeocode: () => void;
  onRemove: () => void;
}) {
  const w = worstLevel(row.issues);
  const bg = w === "error" ? "bg-rose-50" : w === "warning" ? "bg-amber-50" : w === "info" ? "bg-slate-50" : "";
  const ring = row.uploaded ? "opacity-40" : "";

  return (
    <tr className={clsx("border-t", bg, ring)}>
      <td className="p-2">
        <input type="checkbox" checked={row.selected} onChange={onToggle} disabled={row.uploaded} />
      </td>
      <td className="p-2 whitespace-nowrap">
        {row.uploaded ? <span className="chip bg-emerald-100 text-emerald-700 text-[10px]">✓ 업로드</span>
          : w === "error" ? <span className="chip bg-rose-200 text-rose-800 text-[10px]">🚫 오류</span>
          : w === "warning" ? <span className="chip bg-amber-200 text-amber-800 text-[10px]">⚠️ 경고</span>
          : w === "info" ? <span className="chip bg-slate-200 text-slate-700 text-[10px]">ℹ️ 정보</span>
          : <span className="chip bg-emerald-100 text-emerald-700 text-[10px]">✓ 정상</span>}
      </td>
      <td className="p-2 text-[10px] text-slate-500 max-w-[80px] truncate" title={row.sourceFile}>{row.sourceFile}</td>
      <td className="p-2">
        <input className="w-24 bg-transparent border-b border-transparent focus:border-milk-400 outline-none" value={row.data.school || ""} onChange={(e) => onChange({ school: e.target.value })} />
      </td>
      <td className="p-2 font-medium">
        <input className="w-20 bg-transparent border-b border-transparent focus:border-milk-400 outline-none" value={row.data.name || ""} onChange={(e) => onChange({ name: e.target.value })} />
      </td>
      <td className="p-2">
        <input className="w-20 bg-transparent border-b border-transparent focus:border-milk-400 outline-none" value={row.data.guardian || ""} onChange={(e) => onChange({ guardian: e.target.value })} />
      </td>
      <td className="p-2">
        <input
          className="w-32 bg-transparent border-b border-transparent focus:border-milk-400 outline-none"
          value={row.data.phone || ""}
          onChange={(e) => onChange({ phone: e.target.value })}
          onBlur={(e) => onChange({ phone: normalizePhone(e.target.value) })}
        />
      </td>
      <td className="p-2">
        <input
          className="w-80 bg-transparent border-b border-transparent focus:border-milk-400 outline-none"
          value={row.data.refinedAddress || row.data.rawAddress || ""}
          onChange={(e) => onChange({ refinedAddress: e.target.value, rawAddress: e.target.value })}
        />
      </td>
      <td className="p-2 whitespace-nowrap text-[11px]">
        <span className="text-slate-600">{row.data.gu}</span>
        <span className="text-slate-400">/</span>
        <span>{row.data.dong || <em className="text-slate-400">-</em>}</span>
      </td>
      <td className="p-2">
        <span className={`chip text-[10px] ${BRAND_COLORS[row.data.brand as Brand] ?? "bg-slate-100"}`}>{row.data.brand}</span>
      </td>
      <td className="p-2 text-[11px] whitespace-nowrap">
        {row.data.isProxy && <span className="chip bg-amber-100 text-amber-800 text-[9px] mr-1" title={`타사 대행: ${row.data.proxyFor}`}>🚛{row.data.proxyFor}</span>}
        {row.data.company}
      </td>
      <td className="p-2 whitespace-nowrap text-[10px] text-slate-500">
        {row.data.lat ? `${row.data.lat.toFixed(4)},${row.data.lng?.toFixed(4)}` : <span className="text-slate-300">없음</span>}
      </td>
      <td className="p-2 max-w-[250px]">
        {row.issues.length === 0 ? (
          <span className="text-emerald-600">✓</span>
        ) : (
          <ul className="space-y-0.5 leading-tight">
            {row.issues.slice(0, 2).map((i, k) => (
              <li key={k} className={clsx(
                "text-[11px]",
                i.level === "error" ? "text-rose-700" : i.level === "warning" ? "text-amber-700" : "text-slate-500",
              )}>
                • {i.message}
              </li>
            ))}
            {row.issues.length > 2 && <li className="text-[11px] text-slate-400" title={row.issues.slice(2).map((i) => i.message).join(" / ")}>외 {row.issues.length - 2}건</li>}
          </ul>
        )}
      </td>
      <td className="p-2 whitespace-nowrap">
        <button
          title="지도 위치 찾기"
          className="btn-ghost text-[10px]"
          onClick={onGeocode}
          disabled={row.geocoding || row.uploaded}
        >
          {row.geocoding ? "🌐..." : "🌐정리"}
        </button>
        <button title="삭제" className="btn-ghost text-[10px] text-rose-500" onClick={onRemove} disabled={row.uploaded}>✕</button>
      </td>
    </tr>
  );
}
